import WorkoutData, { HolderType, WorkoutDataState, WorkoutType } from '../data/workout-data'
import Station from '../data/station'
import { WorkoutExercise } from '@src/data/workout-exercise'
import { API } from '@src/lib/api_path'
import ApiClient from '@src/lib/api_client'
import camelcaseKeys from 'camelcase-keys'
import SessionManager from '@src/lib/session_manager'
import WorkoutResults from '@src/data/workout-results'
import { ExerciseResponse } from '@src/data/exercise-response'
import { NO_EQUIPMENT } from '@src/util/constants'

type WorkoutsResponse = { workouts: WorkoutData[] }
type WorkoutsResultsResponse = { workout_results: WorkoutResults, meta: { current_page: number, total_pages: number} }

/**
 * Service utility dealing with workouts.
 */
export class WorkoutService {

  apiClient: ApiClient = new ApiClient()

  /**
   * Get the station id on which an exercise will be performed.
   *
   * @param exerciseId - exercise id
   * @param stations - list of stations on which exercises will be performed
   */
  static getStationIdForExercise (exerciseId: number, stations: Station[]) {
    for (let i = 0; i < stations.length; i++) {
      for (let j = stations[i].exercises.length - 1; j >= 0; j--) {
        if (stations[i].exercises[j].exercise.id === exerciseId) {
          stations[i].exercises.splice(j,1) // remove the exercise so the same exercise can be added to a different station
          return stations[i].id
        }
      }
    }
    return null
  }

  /**
   * Build a workout DTO to be submitted to server.
   *
   * @param workout - workout data
   * @param exercises - list of exercises to be set to the workout
   * @param screenExerciseLen - club exercise duration (to be used to determine the number of repetitions)
   * @param stations - list of stations on which exercises will be performed
   * @param holderType - club or member owned
   * @param clubId - club id (to which the workout will the assigned to)
   * @param memberId - member id (to which to workout will be assigned to)
   */
  buildWorkout (workout: WorkoutData, exercises, screenExerciseLen: number, stations: Station[], holderType: HolderType, clubId: number, memberId: number | null): any {
    // build workout object
    if (workout.type === WorkoutType.HOME) {
      return {
        name: workout.name,
        type: workout.type,
        featured: false,
        holder_type: holderType,
        holder_id: holderType === HolderType.CLUB ? clubId : memberId,
        workout_exercises: this.formatExercises(exercises, screenExerciseLen, stations, false),
        categories: workout.categories
      }
    } else {
      return {
        name: workout.name,
        type: workout.type,
        // the user can set the workout as station locked, but if no exercise - station mapping was done, consider the workout as not 'station locked'
        station_locked: workout.stationLocked,
        continuous_workout: workout.continuousWorkout,
        circle_workout: workout.circleWorkout,
        restart_if_uncompleted: workout.restartIfUncompleted,
        featured: false,
        holder_type: holderType,
        holder_id: holderType === HolderType.CLUB ? clubId : memberId,
        workout_exercises: this.formatExercises(exercises, screenExerciseLen, stations, workout.stationLocked),
        categories: workout.categories
      }
    }
  }

  /**
   * Format exercises DTO to be submitted to server (attached to the workout).
   *
   * @param workoutExercises - list of exercises to be set to the workout
   * @param screenExerciseLen - club exercise duration (to be used to determine the number of repetitions)
   * @param stations - list of stations on which exercises will be performed
   */
  formatExercises (workoutExercises, screenExerciseLen: number, stations: Station[], isStationDependent: boolean) {
    let exercises: any[] = []
    workoutExercises.forEach(workoutExerciseData => {
      let workoutExercise
      if (!(workoutExerciseData instanceof WorkoutExercise)) {
        workoutExercise = new WorkoutExercise(workoutExerciseData)
      } else {
        workoutExercise = workoutExerciseData
      }
      exercises.push({
        exercise_id: workoutExercise.exercise.id,
        station_id: isStationDependent ? WorkoutService.getStationIdForExercise(workoutExercise.exercise.id, stations) || workoutExercise.stationId : null,
        repetitions: Math.round(workoutExercise.getRepetitionsCount(screenExerciseLen)) || 0,
        repetition_duration: workoutExercise.repetitionDuration,
        weight: workoutExercise.weight || 0,
        side: workoutExercise.side || null,
        subsequent_rest_period: workoutExercise.subsequentRestPeriod || 0,
        breakpoint_after_exercise: workoutExercise.breakpointAfterExercise
      })
    })
    return exercises
  }

  formatExercisesFromServer (workoutExercises) {
    return workoutExercises.map((workoutExercise) => {
      const formattedWorkoutExercise: WorkoutExercise = new WorkoutExercise(camelcaseKeys(workoutExercise, { deep: true }))
      const exercise = new ExerciseResponse(camelcaseKeys(workoutExercise.exercise, { deep: true }))
      formattedWorkoutExercise.setExercise(exercise)
      return formattedWorkoutExercise
    })
  }

  /**
   * Build the workout composition out of the selected tags. Each appearance of a tag is equal to 20% of the tag's progress circle around it.
   * @param selectedExercises - list of workout exercises used to extract exercise tags and calculate progress
   */
  buildWorkoutComposition (selectedExercises: WorkoutExercise[]) {
    let workoutComposition: { [key: string]: number } = {}
    selectedExercises.forEach(workoutExercise => {
      if (workoutExercise && workoutExercise.exercise) {
        workoutExercise.exercise.exerciseTags.forEach(tag => {
          // rotate twist tag comes in different formats
          let tagKey = tag.key === 'rotate/twist' ? 'rotate_twist' : tag.key
          this.addToComposition(workoutComposition, tagKey.toLowerCase())
        })
        workoutExercise.exercise.equipment.forEach(tag => this.addToComposition(workoutComposition, tag.toLowerCase()))
        if (!workoutExercise.exercise.equipment.length) this.addToComposition(workoutComposition, NO_EQUIPMENT)
      }
    })
    return workoutComposition
  }

  addToComposition (workoutComposition , tag: string) {
    if (workoutComposition[tag] === undefined) {
      workoutComposition[tag] = 20
    } else if (workoutComposition[tag] < 100) {
      workoutComposition[tag] += 20
    }
  }

  async loadWorkouts (memberId?) {
    let promises: Promise<WorkoutData[]>[] = []

    if (SessionManager.Instance.isManager()) {
      promises.push(this.loadClubWorkouts())

      if (!SessionManager.Instance.isMasterClub()) {
        promises.push(this.loadMasterWorkouts().then(result => result.filter((wo) => wo.state === WorkoutDataState.PUBLISHED)))
      }

      if (memberId) promises.push(this.loadMemberTrainerWorkouts(memberId))
    } else {
      promises.push(this.loadMemberWorkouts())
    }
    return [].concat.apply([], await Promise.all(promises))

  }

  public async getDefaultWorkout () {
    let allWorkouts: WorkoutData[] = []
    await this.loadWorkouts().then(workouts => allWorkouts = workouts ? workouts : [])
    let defaultWorkouts = allWorkouts.filter(workout => workout.default)
    return defaultWorkouts.length > 0 ? defaultWorkouts[0] : null
  }

  delete (workoutId: number, memberId: number = 0): Promise<void> {
    let path = `${SessionManager.Instance.isManager() ? API.TRAINERS_API.WORKOUTS : API.MEMBERS_API.WORKOUTS}/${workoutId}`
    if (memberId) path = `${path}?member_id=${memberId}`
    return this.apiClient.delete<any>(path)
      .then(() => Promise.resolve())
      .catch(error => Promise.reject(error))
  }

  getWorkout (workoutId: number) {
    return this.apiClient.get<any>(`${SessionManager.Instance.isManager() ? API.TRAINERS_API.WORKOUTS : API.MEMBERS_API.WORKOUTS}/${workoutId}`)
      .then(response => {
        return Promise.resolve(response)
      })
      .catch(error => {
        return Promise.reject(error)
      })
  }

  getWorkoutResults (memberId: string, page: number, elementsPerPage: number, startDate: any, endDate: any, includeExerciseResults: boolean = false) {
    let api = SessionManager.Instance.isManager() ? API.TRAINERS_API.WORKOUT_RESULTS.replace('{memberId}', memberId) : API.MEMBERS_API.WORKOUT_RESULTS
    return this.apiClient.get<WorkoutsResultsResponse>(api,
      {
        start_date: startDate,
        end_date: endDate,
        page,
        items_per_page: elementsPerPage,
        include_exercise_results: includeExerciseResults
      })
      .then(response => {
        return Promise.resolve({
          workoutResults: response.workout_results.station.map(item => camelcaseKeys(item, { deep: true })),
          totalPages: response.meta.total_pages
        })
      })
      .catch(error => {
        console.error('failed to retrieve workout results', error)
        return Promise.reject(error)
      })
  }

  rateWorkout (workoutId, intensityRating) {
    return this.apiClient
      .post(API.MEMBERS_API.WORKOUT_RATE, { result_id: workoutId, intensity_rating: intensityRating, result_type: 'station' })
      .then(() => Promise.resolve())
      .catch(error => {
        console.log('cannot rate workout', error)
        return Promise.reject(error)
      })
  }

  activateWorkout (workoutId, memberId) {
    return this.apiClient
      .post((SessionManager.Instance.isManager() ? API.TRAINERS_API.WORKOUT_ACTIVATE : API.MEMBERS_API.WORKOUT_ACTIVATE).replace('{workoutId}', `${workoutId}`).replace('{memberId}', `${memberId}`), {})
      .then(() => Promise.resolve(workoutId))
      .catch(error => {
        console.log('cannot activate workout', error)
        return Promise.reject(error)
      })
  }

  add (workout, originalWorkoutId?) {
    const reqBody = (originalWorkoutId) ? { workout: { ...workout, original_workout_id: originalWorkoutId } } : { workout: workout }
    return this.apiClient.post(SessionManager.Instance.isManager() ? API.TRAINERS_API.WORKOUTS : API.MEMBERS_API.WORKOUTS, reqBody)
      .then((response: any) => Promise.resolve(response.workout.id))
      .catch(error => {
        console.log('cannot add workout', error)
        return Promise.reject(error)
      })
  }

  edit (workoutId, workout) {
    return this.apiClient.put(`${SessionManager.Instance.isManager() ? API.TRAINERS_API.WORKOUTS : API.MEMBERS_API.WORKOUTS}/${workoutId}`, { workout: workout })
      .then((response: any) => Promise.resolve(response.workout.id))
      .catch(error => {
        console.log('cannot edit workout', error)
        return Promise.reject(error)
      })
  }

  toggleVisibility (workoutId: number, memberId: number = 0) {
    let path = API.TRAINERS_API.WORKOUT_TOGGLE_VISIBILITY.replace('{workoutId}', `${workoutId}`)
    if (memberId) path = `${path}?member_id=${memberId}`
    return this.apiClient.post(path, {})
      .then(() => Promise.resolve())
      .catch(error => {
        console.log('cannot edit workout', error)
        return Promise.reject(error)
      })
  }

  private loadMasterWorkouts (): Promise<WorkoutData[]> {
    return this.apiClient.get<WorkoutsResponse>(`${API.TRAINERS_API.WORKOUTS}?scope=master`)
      .then(response => {
        return response.workouts.map((workout: any) => new WorkoutData(camelcaseKeys(workout, { deep: true })))
      })
      .catch(error => {
        console.error('failed to retrieve workouts', error)
        return Promise.reject(error)
      })
  }

  private loadClubWorkouts (): Promise<WorkoutData[]> {
    return this.apiClient.get<WorkoutsResponse>(`${API.TRAINERS_API.WORKOUTS}?scope=club`)
      .then(response => {
        return response.workouts.map((workout: any) => new WorkoutData(camelcaseKeys(workout, { deep: true })))
      })
      .catch(error => {
        console.error('failed to retrieve workouts', error)
        return Promise.reject(error)
      })
  }

  private loadMemberWorkouts (): Promise<WorkoutData[]> {
    return this.apiClient.get<WorkoutsResponse>(API.MEMBERS_API.WORKOUTS)
      .then(response => {
        return response.workouts.filter((workout) => {
          let hiddenClubWorkout = workout.hidden && workout['holder_type'] === HolderType.CLUB
          return hiddenClubWorkout || (!workout.hidden)
        }).map((workout: any) => new WorkoutData(camelcaseKeys(workout, { deep: true })))
      })
      .catch(error => {
        console.error('failed to retrieve workouts', error)
        return Promise.reject(error)
      })
  }

  private loadMemberTrainerWorkouts (memberId): Promise<WorkoutData[]> {
    return this.apiClient.get<WorkoutsResponse>(API.TRAINERS_API.MEMBER_WORKOUTS.replace('{memberId}', memberId))
      .then(response => {
        return response.workouts.map((workout: any) => new WorkoutData(camelcaseKeys(workout, { deep: true })))
      })
      .catch(error => {
        console.error('failed to retrieve workouts', error)
        return Promise.reject(error)
      })
  }
}
